import { useCallback, useEffect, useState } from "react";
import { Box, Button, Drawer, drawerClasses, IconButton, styled, Typography } from "~/common/mui";
import { Close } from "@mui/icons-material";

import { Chip } from "~/common/mui/Chip/Chip";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { verticalStacker } from "~/styles/styledMixins";
import {
  isTechValueFilterCategory,
  type TechValueFilterCategoryData,
} from "~/features/search/models/filterConfigModels";
import { useTranslation } from "react-i18next";
import { selectAppliedTechFilterValues, selectBreadcrumbs } from "~/features/search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { BreadcrumbType } from "~/utils/models";
import { applyAllFiltersAction } from "~/features/search/redux/actions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { InnerTechFilterCategory } from "~/features/search/components/Filters/components/TechFilter/TechFilterCategory";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { DialogActions, DialogContent } from "~/common/mui/Dialog/Dialog";

export const TechCategoryChip = ({ index }: { index: number }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMinTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const dispatch = useDispatch();

  const techFilterCategory: TechValueFilterCategoryData | undefined =
    useSelector(selectVisibleFilters).filter(isTechValueFilterCategory)[index];
  const breadcrumbs = useSelector(selectBreadcrumbs);
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    if (isDrawerOpen) {
      methods.reset();
    }
  }, [appliedTechFilterValues, methods, isDrawerOpen]);

  const allValues = useWatch({ control: methods.control });
  const isClearEnabled =
    techFilterCategory?.items.some(
      (item) => allValues[item.uid]?.["min"] !== undefined || allValues[item.uid]?.["max"] !== undefined,
    ) ?? false;

  const onClearClick = () => {
    const filterUids = techFilterCategory.items.map((item) => item.uid);
    resetFilters(filterUids);
    setIsDrawerOpen(false);
  };

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
    setIsDrawerOpen(false);
  };

  const resetFilters = useCallback(
    (filterUids?: string[]) => {
      const currentValues = methods.getValues();

      // If specific UIDs provided, clear only those. Otherwise, get all tech filter UIDs from current values
      const uidsToReset = filterUids || Object.keys(currentValues);

      uidsToReset.forEach((uid) => {
        methods.setValue(`${uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
        methods.setValue(`${uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
      });

      // Submit the form to trigger the search
      methods.handleSubmit(onSubmit)();
    },
    [methods, onSubmit],
  );

  const applyCb = methods.handleSubmit(onSubmit);

  if (!techFilterCategory) {
    return null;
  }

  const validTechValueCodes = techFilterCategory.items.flatMap((item) => item.uid);

  const breadcrumbLength = breadcrumbs.filter((breadcrumb) => {
    if (breadcrumb.type === BreadcrumbType.TECH_VALUE) {
      return validTechValueCodes.includes(breadcrumb.code);
    } else {
      return false;
    }
  }).length;
  let label = t(techFilterCategory.title);
  if (breadcrumbLength > 0) {
    label += ` (${breadcrumbLength})`;
  }

  let onClear = undefined;
  if (breadcrumbLength > 0) {
    onClear = () => {
      const filterUids = techFilterCategory.items.map((item) => item.uid);
      resetFilters(filterUids);
    };
  }

  return (
    <>
      <Chip
        variant={"filter"}
        onClick={() => setIsDrawerOpen(true)}
        label={label}
        selected={breadcrumbLength > 0}
        onDelete={onClear}
        deleteIcon={<CloseIcon />}
      />

      <StyledDrawer anchor={isMinTablet ? "left" : "bottom"} open={isDrawerOpen} onClose={handleClose}>
        <DrawerContent sx={{ ...verticalStacker("24px") }}>
          <DrawerHeader>
            <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
              {t(techFilterCategory.title)}
            </Typography>
            <IconButton onClick={handleClose} color={"secondary"} variant={"contained"}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <FormProvider {...methods}>
            <form onSubmit={applyCb}>
              <Box sx={{ ...verticalStacker(), gap: "32px" }}>
                <InnerTechFilterCategory data={techFilterCategory} />
              </Box>
              <button hidden type={"submit"} />
            </form>
          </FormProvider>
        </DrawerContent>

        <DrawerActions>
          <Button
            variant={"text"}
            fullWidth
            disabled={!isClearEnabled}
            onClick={onClearClick}
            data-test-id={`tech-filter-category-${techFilterCategory.facetId}-reset`}
          >
            {t("searchPage.refinement.button.clear")}
          </Button>
          <Button
            disabled={!methods.formState.isDirty}
            fullWidth
            data-test-id={`tech-filter-category-${techFilterCategory.facetId}-apply`}
            onClick={applyCb}
          >
            {t("searchPage.refinement.button.apply")}
          </Button>
        </DrawerActions>
      </StyledDrawer>
    </>
  );
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    [theme.breakpoints.down("tablet")]: {
      height: "70vh",
      maxHeight: "700px",
    },
    [theme.breakpoints.between("tablet", "desktop-s")]: {
      width: "45vw",
      maxWidth: "550px",
    },
  },
}));

const DrawerHeader = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const DrawerContent = styled(DialogContent)({
  padding: "16px",
});

const DrawerActions = styled(DialogActions)({
  padding: "8px 16px",
  boxShadow: STICKY_TO_BOTTOM,
});
